import { useMemo } from "react";

import type { SidebarNavigationItem } from "@/routing/adminRoutes";
import { useGetAdminV2LiveAlertsCountQuery } from "@/store/api/baseApi";
import { LIVE_ALERTS_COUNT_POLL_MS } from "@/store/api/endpoints/adminV2LiveAlertCountEndpoints";

/** The navigation map's Live alerts page id. */
export const LIVE_ALERTS_NAV_PAGE_ID = "live_alerts";

/**
 * Navigation PRD §6 / F-198: Live alerts carries the number of alerts for the
 * signed-in admin, polled from the count read (cached server-side for 45s;
 * the admin's own actions read it fresh). Zero shows no badge. Staff who
 * cannot read live alerts, or whose sidebar has no Live alerts page, never
 * request it.
 */
export function useSidebarBadges(
  items: SidebarNavigationItem[],
  canRead: boolean,
): SidebarNavigationItem[] {
  const hasPage = items.some((section) =>
    section.children?.some((page) => page.id === LIVE_ALERTS_NAV_PAGE_ID),
  );
  const { currentData, isError } = useGetAdminV2LiveAlertsCountQuery(
    undefined,
    {
      pollingInterval: LIVE_ALERTS_COUNT_POLL_MS,
      skip: !canRead || !hasPage,
      skipPollingIfUnfocused: true,
    },
  );
  const count = canRead && hasPage && !isError ? (currentData?.count ?? 0) : 0;
  return useMemo(
    () =>
      count > 0
        ? items.map((section) =>
            section.children?.some(
              (page) => page.id === LIVE_ALERTS_NAV_PAGE_ID,
            )
              ? {
                  ...section,
                  children: section.children.map((page) =>
                    page.id === LIVE_ALERTS_NAV_PAGE_ID
                      ? { ...page, badge: count }
                      : page,
                  ),
                }
              : section,
          )
        : items,
    [count, items],
  );
}
